import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowRight, ArrowDown, Check, CheckCircle2, ChevronDown, ChevronRight,
    Users, UserPlus, ShoppingCart, Gift, Truck, Eye, Crown, Megaphone, LayoutGrid,
    Target, BarChart3, Sparkles, Zap, Filter, Flame, Video, Split,
    Paintbrush, Headphones, Plug, Search, SlidersHorizontal, HelpCircle
} from 'lucide-react';
import Header from '../../Components/Header';
import Footer from '../../Components/Footer';

/* ------------------------------------------------------------------
   히어로 데모: 같은 페이지, 방문자별로 다른 내용
   Tailwind purge 회피를 위해 클래스는 전부 정적 문자열로 둔다
   ------------------------------------------------------------------ */
const HERO_SEGMENTS = [
    {
        key: 'guest',
        short: '비회원',
        who: '회원가입 안 한 고객',
        signal: '비회원 · 첫 방문',
        eyebrow: 'WELCOME',
        title: '지금 가입하면 5,000원 쿠폰',
        cta: '10초 만에 가입하기',
        tipLabel: '가입 혜택 배너',
        tipSub: '비회원에게만 보입니다',
        classes: {
            banner: 'bg-gradient-to-r from-pink-500 to-rose-500',
            eyebrow: 'text-pink-100',
            title: 'text-white',
            cta: 'bg-white text-pink-700',
            dot: 'bg-pink-400',
            chip: 'bg-pink-500/10 text-pink-300 border-pink-500/30',
        },
    },
    {
        key: 'cart',
        short: '장바구니 이탈',
        who: '장바구니 담고 구매 안 한 고객',
        signal: '장바구니 1개 · 구매 이력 없음',
        eyebrow: 'ONLY TODAY',
        title: '담아두신 셔츠, 오늘까지 10% 할인',
        cta: '장바구니 보기',
        tipLabel: '장바구니 할인 배너',
        tipSub: '담고 떠난 고객에게만 보입니다',
        classes: {
            banner: 'bg-gradient-to-r from-orange-500 to-amber-500',
            eyebrow: 'text-orange-100',
            title: 'text-white',
            cta: 'bg-white text-orange-600',
            dot: 'bg-orange-400',
            chip: 'bg-orange-500/10 text-orange-300 border-orange-500/30',
        },
    },
    {
        key: 'vip',
        short: 'VIP',
        who: 'VIP 단골 고객',
        signal: 'VIP 등급 · 구매 12회',
        eyebrow: 'VIP ONLY',
        title: '신상품, VIP 고객님께 먼저 공개합니다',
        cta: '먼저 보기',
        tipLabel: '할인 배너 대신 VIP 혜택',
        tipSub: 'VIP 등급에게만 보입니다',
        classes: {
            banner: 'bg-gradient-to-r from-gray-800 to-gray-900',
            eyebrow: 'text-amber-400',
            title: 'text-amber-100',
            cta: 'bg-amber-400 text-gray-900',
            dot: 'bg-amber-400',
            chip: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
        },
    },
    {
        key: 'ad',
        short: '광고 유입',
        who: '광고 보고 처음 온 고객',
        signal: '인스타그램 광고 · 여름 원피스',
        eyebrow: 'SUMMER COLLECTION',
        title: '광고에서 보신 여름 원피스 기획전',
        cta: '기획전 바로가기',
        tipLabel: '광고 소재와 같은 기획전',
        tipSub: '해당 광고로 온 고객에게만',
        classes: {
            banner: 'bg-gradient-to-r from-sky-500 to-indigo-500',
            eyebrow: 'text-sky-100',
            title: 'text-white',
            cta: 'bg-white text-indigo-600',
            dot: 'bg-sky-400',
            chip: 'bg-sky-500/10 text-sky-300 border-sky-500/30',
        },
    },
];

/* ------------------------------------------------------------------
   고객 유형별 활용 사례
   ------------------------------------------------------------------ */
const SEGMENT_CASES = [
    {
        icon: <UserPlus className="w-5 h-5" />,
        who: '회원가입 안 한 고객',
        eyebrow: 'WELCOME',
        preview: '지금 가입하면 5,000원 쿠폰',
        desc: '가입하면 뭐가 좋은지 첫 화면에서 바로 보여주고, 가입 버튼까지 한 번에 연결하세요.',
        shows: ['가입 혜택 배너', '가입 쿠폰 안내'],
        classes: {
            hover: 'hover:border-pink-200',
            iconBox: 'bg-pink-100 text-pink-600',
            banner: 'bg-gradient-to-r from-pink-500 to-rose-500',
            eyebrow: 'text-pink-100',
            text: 'text-white',
            check: 'text-pink-500',
        },
    },
    {
        icon: <ShoppingCart className="w-5 h-5" />,
        who: '장바구니 담고 구매 안 한 고객',
        eyebrow: 'ONLY TODAY',
        preview: '담아두신 상품, 오늘까지 10% 할인',
        desc: '마음은 있는데 망설이는 고객입니다. 마지막 한 걸음을 밀어줄 혜택을 보여주세요.',
        shows: ['장바구니 상품 할인', '마감 임박 쿠폰'],
        classes: {
            hover: 'hover:border-orange-200',
            iconBox: 'bg-orange-100 text-orange-600',
            banner: 'bg-gradient-to-r from-orange-500 to-amber-500',
            eyebrow: 'text-orange-100',
            text: 'text-white',
            check: 'text-orange-500',
        },
    },
    {
        icon: <Gift className="w-5 h-5" />,
        who: '가입만 하고 첫 구매 안 한 고객',
        eyebrow: 'FIRST ORDER',
        preview: '첫 구매 고객 전용 무료배송',
        desc: '가입까지 했다면 관심은 확실합니다. 첫 구매를 해야 할 이유를 만들어 주세요.',
        shows: ['첫 구매 전용 쿠폰', '무료배송 혜택'],
        classes: {
            hover: 'hover:border-violet-200',
            iconBox: 'bg-violet-100 text-violet-600',
            banner: 'bg-gradient-to-r from-violet-500 to-fuchsia-500',
            eyebrow: 'text-violet-100',
            text: 'text-white',
            check: 'text-violet-500',
        },
    },
    {
        icon: <Truck className="w-5 h-5" />,
        who: '무료배송까지 조금 모자란 고객',
        eyebrow: 'ALMOST THERE',
        preview: '3,000원만 더 담으면 무료배송',
        desc: '남은 금액을 알려주는 것만으로 한 개 더 담게 됩니다. 객단가가 함께 올라갑니다.',
        shows: ['무료배송 잔액 안내', '추가 구매 유도'],
        classes: {
            hover: 'hover:border-teal-200',
            iconBox: 'bg-teal-100 text-teal-600',
            banner: 'bg-gradient-to-r from-teal-500 to-cyan-500',
            eyebrow: 'text-teal-100',
            text: 'text-white',
            check: 'text-teal-500',
        },
    },
    {
        icon: <Eye className="w-5 h-5" />,
        who: '상품만 보고 계속 그냥 나가는 고객',
        eyebrow: 'WELCOME BACK',
        preview: '다시 오셨네요! 재방문 고객 전용 혜택',
        desc: '여러 번 둘러보기만 했다면 확신이 부족한 겁니다. 혜택과 함께 베스트 리뷰를 보여주세요.',
        shows: ['재방문 고객 전용 혜택', '베스트 리뷰 노출'],
        classes: {
            hover: 'hover:border-emerald-200',
            iconBox: 'bg-emerald-100 text-emerald-600',
            banner: 'bg-gradient-to-r from-emerald-500 to-teal-500',
            eyebrow: 'text-emerald-100',
            text: 'text-white',
            check: 'text-emerald-500',
        },
    },
    {
        icon: <Crown className="w-5 h-5" />,
        who: 'VIP · 단골 고객',
        eyebrow: 'VIP ONLY',
        preview: '신상품, VIP 고객님께 먼저 공개합니다',
        desc: '단골에게 첫 구매 할인 배너는 의미가 없습니다. 등급에 맞는 특별함을 보여주세요.',
        shows: ['등급 전용 혜택', '신상품 우선 공개', '할인 배너 숨기기'],
        classes: {
            hover: 'hover:border-amber-200',
            iconBox: 'bg-amber-100 text-amber-600',
            banner: 'bg-gradient-to-r from-gray-800 to-gray-900',
            eyebrow: 'text-amber-400',
            text: 'text-amber-100',
            check: 'text-amber-500',
        },
    },
    {
        icon: <Megaphone className="w-5 h-5" />,
        who: '광고 보고 처음 들어온 고객',
        eyebrow: 'SUMMER COLLECTION',
        preview: '광고에서 보신 여름 원피스 기획전',
        desc: '광고에서 본 그 상품을 첫 화면에 바로 보여주세요. 다시 검색할 필요가 없습니다.',
        shows: ['광고 소재별 맞춤 화면', '기획전 바로 노출'],
        classes: {
            hover: 'hover:border-indigo-200',
            iconBox: 'bg-indigo-100 text-indigo-600',
            banner: 'bg-gradient-to-r from-sky-500 to-indigo-500',
            eyebrow: 'text-sky-100',
            text: 'text-white',
            check: 'text-indigo-500',
        },
    },
    {
        icon: <LayoutGrid className="w-5 h-5" />,
        who: '특정 카테고리를 둘러본 고객',
        eyebrow: 'FOR YOU',
        preview: '관심 있으실 아우터 신상품 모음',
        desc: '둘러본 카테고리를 기억했다가 다음 화면에서 관련 기획전으로 이어주세요.',
        shows: ['카테고리 맞춤 기획전', '관련 신상품 노출'],
        classes: {
            hover: 'hover:border-sky-200',
            iconBox: 'bg-sky-100 text-sky-600',
            banner: 'bg-gradient-to-r from-cyan-500 to-sky-600',
            eyebrow: 'text-cyan-100',
            text: 'text-white',
            check: 'text-sky-500',
        },
    },
];

const EXTRA_CONDITIONS = ['디바이스', '브라우저', 'OS', '지역', '유입 경로', '트래픽 소스', '신규 · 재방문', 'URL 파라미터', '요일', '시간대'];

const FAQS = [
    {
        q: '개발자가 없어도 쓸 수 있나요?',
        a: '화면 수정은 비주얼 에디터에서 클릭으로 직접 하실 수 있습니다. 회원 여부, 장바구니, 구매 이력, 등급처럼 쇼핑몰마다 구조가 다른 조건은 전담 매니저가 사이트에 맞게 직접 세팅해 드립니다.',
    },
    {
        q: 'A/B 테스트와 무엇이 다른가요?',
        a: 'A/B 테스트는 "어떤 화면이 더 나은지"를 찾는 것이고, 개인화는 "누구에게 어떤 화면을 보여줄지"를 정하는 것입니다. 개인화도 A/B 테스트 메뉴에서 같은 에디터와 같은 타게팅 조건으로 만들기 때문에 따로 배울 것이 없습니다.',
    },
    {
        q: '사이트가 느려지지 않나요?',
        a: '이미 설치된 SWITCHUP 스크립트 하나로 동작합니다. 개인화를 위해 추가로 설치할 코드는 없습니다.',
    },
    {
        q: '카페24 · 아임웹 쇼핑몰에서도 되나요?',
        a: '네. 카페24 · 아임웹 앱으로 SWITCHUP을 설치한 쇼핑몰이라면 바로 시작할 수 있습니다.',
    },
    {
        q: '요금은 어떻게 되나요?',
        a: 'A/B 테스트와 동일하게 방문자(트래픽) 기준으로 차감됩니다. 개인화는 계속 켜두는 캠페인이라 트래픽이 꾸준히 사용된다는 점을 참고해 주세요.',
    },
];

const ServicePersonalization = () => {
    const [scrolled, setScrolled] = useState(false);
    const [segIdx, setSegIdx] = useState(0);
    const [reduceMotion, setReduceMotion] = useState(false);
    const [openFaq, setOpenFaq] = useState(0);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        setReduceMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }, []);

    // 히어로 방문자 자동 전환 — 수동으로 고르면 타이머가 다시 시작된다
    useEffect(() => {
        if (reduceMotion) return;
        const id = setTimeout(() => setSegIdx(i => (i + 1) % HERO_SEGMENTS.length), 3500);
        return () => clearTimeout(id);
    }, [segIdx, reduceMotion]);

    const seg = HERO_SEGMENTS[segIdx];

    return (
        // keep-all: 한글이 글자 단위로 끊기지 않고 어절 단위로 줄바꿈되게
        <div className="bg-white" style={{ wordBreak: 'keep-all' }}>
            {/* 플로팅 CTA 버튼 */}
            <div className={`fixed bottom-6 left-6 z-50 transition-all duration-300 ${scrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <Link to="/freestart">
                    <button className="flex items-center bg-gradient-to-r from-pink-600 to-rose-600 text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group">
                        <span className="font-medium">무료로 시작하기</span>
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </button>
                </Link>
            </div>

            <Header />

            {/* ==========================================
          섹션 1: Hero Section
      ========================================== */}
            <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-[#0B0F19]">
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:40px_40px]"></div>
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-pink-600/10 rounded-full blur-[120px] pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-rose-600/10 rounded-full blur-[100px] pointer-events-none"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">

                        {/* Left: 텍스트 */}
                        <div className="max-w-2xl">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-400 text-sm font-semibold mb-8">
                                <Sparkles className="w-4 h-4" />
                                <span>Personalization</span>
                            </div>

                            <h1 className="text-3xl sm:text-5xl font-bold text-white mb-6 leading-tight tracking-tight">
                                모든 고객에게 같은 화면, <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-rose-400 to-orange-300">아직도 보여주시나요?</span>
                            </h1>

                            <p className="text-base sm:text-xl text-gray-400 mb-10 leading-relaxed max-w-lg">
                                회원가입 안 한 고객, 장바구니만 담고 떠난 고객, 다시 찾아온 단골.
                                같은 페이지에서 고객마다 다른 내용을 보여주세요.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/freestart">
                                    <button className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-500 hover:to-rose-500 text-white rounded-xl font-bold text-lg shadow-lg shadow-pink-900/20 hover:shadow-pink-500/30 transition-all hover:-translate-y-1">
                                        무료로 개인화 시작하기
                                    </button>
                                </Link>
                                <Link to="/contact">
                                    <button className="w-full sm:w-auto px-8 py-4 bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-xl font-medium text-lg transition-all backdrop-blur-sm flex items-center justify-center gap-2 group">
                                        도입 상담 받기
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </Link>
                            </div>

                            <div className="mt-8 flex items-center gap-3 text-sm text-gray-500">
                                <div className="w-8 h-8 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center text-pink-400">
                                    <Headphones className="w-4 h-4" />
                                </div>
                                <p>쇼핑 행동 기반 조건은 전담 매니저가 직접 세팅해 드려요</p>
                            </div>
                        </div>

                        {/* Right: 3D 개인화 데모 */}
                        <div className="relative perspective-1000 hidden lg:block">
                            <div className="relative transform rotate-y-[-12deg] rotate-x-[5deg] transition-transform duration-700 hover:rotate-y-[-5deg] hover:rotate-x-[2deg]">
                                <div className="bg-[#1A1F2E] border border-gray-700/50 rounded-2xl shadow-2xl overflow-hidden min-h-[500px] relative">

                                    {/* Browser Header */}
                                    <div className="bg-[#0F131C] px-4 py-3 flex items-center gap-2 border-b border-gray-800">
                                        <div className="flex gap-1.5">
                                            <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                                            <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                                            <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                                        </div>
                                        <div className="ml-4 bg-[#1A1F2E] px-3 py-1 rounded text-[10px] text-gray-500 font-mono flex-1 text-center">
                                            shop.example.com
                                        </div>
                                        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-pink-500/10 border border-pink-500/20">
                                            <div className="w-1.5 h-1.5 rounded-full bg-pink-400 animate-pulse"></div>
                                            <span className="text-[9px] font-bold text-pink-400 tracking-wider">PERSONALIZED</span>
                                        </div>
                                    </div>

                                    {/* 현재 방문자 */}
                                    <div className="px-5 py-3 flex items-center justify-between border-b border-gray-800">
                                        <div key={`who-${seg.key}`} className="flex items-center gap-3 seg-in">
                                            <div className="w-7 h-7 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-300">
                                                <Users className="w-3.5 h-3.5" />
                                            </div>
                                            <div>
                                                <div className="text-[10px] text-gray-500">지금 접속한 방문자</div>
                                                <div className="text-xs font-bold text-gray-200">{seg.who}</div>
                                            </div>
                                        </div>
                                        <span key={`sig-${seg.key}`} className={`px-2 py-0.5 rounded-full border text-[10px] font-medium seg-in ${seg.classes.chip}`}>
                                            {seg.signal}
                                        </span>
                                    </div>

                                    {/* 쇼핑몰 화면 */}
                                    <div className="relative mx-5 mt-4 h-[268px] bg-white rounded-lg overflow-hidden border border-gray-700">
                                        <div className="h-7 bg-white border-b border-gray-200 flex items-center px-3 gap-3">
                                            <div className="flex items-center gap-1">
                                                <div className="w-3.5 h-3.5 rounded bg-pink-500"></div>
                                                <span className="text-[8px] font-black text-gray-900 tracking-tight">SWITCH SHOP</span>
                                            </div>
                                            <div className="flex gap-2 text-[7px] text-gray-500 font-medium">
                                                <span>NEW</span><span className="text-gray-900 font-bold">BEST</span><span>아우터</span><span>상의</span><span>하의</span>
                                            </div>
                                            <div className="ml-auto flex items-center gap-2 text-gray-400">
                                                <Search className="w-2.5 h-2.5" />
                                                <ShoppingCart className="w-2.5 h-2.5" />
                                            </div>
                                        </div>

                                        {/* 개인화 배너 — 방문자에 따라 바뀌는 영역 */}
                                        <div className="p-3">
                                            <div key={`banner-${seg.key}`} className={`relative h-[92px] rounded-lg px-4 flex items-center justify-between overflow-hidden seg-in ring-2 ring-pink-400/0 ${seg.classes.banner}`}>
                                                <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10"></div>
                                                <div className="absolute right-14 -bottom-8 w-16 h-16 rounded-full bg-white/10"></div>
                                                <div className="relative z-10">
                                                    <div className={`text-[7px] font-bold tracking-widest ${seg.classes.eyebrow}`}>{seg.eyebrow}</div>
                                                    <div className={`text-[13px] font-black leading-tight mt-1 max-w-[220px] ${seg.classes.title}`}>{seg.title}</div>
                                                    <div className={`inline-block mt-2 px-2 py-1 rounded text-[7px] font-bold ${seg.classes.cta}`}>{seg.cta}</div>
                                                </div>
                                            </div>
                                            <div className="mt-1.5 flex items-center gap-1 text-[7px] text-pink-600 font-bold">
                                                <Sparkles className="w-2.5 h-2.5" /> 이 영역은 방문자마다 다르게 보입니다
                                            </div>
                                        </div>

                                        {/* 상품 목록 (공통) */}
                                        <div className="px-3">
                                            <div className="text-[7px] font-bold text-gray-700 mb-1">BEST 상품</div>
                                            <div className="grid grid-cols-4 gap-2">
                                                {[
                                                    { name: '코튼 오버핏 셔츠', price: '39,000' },
                                                    { name: '린넨 와이드 팬츠', price: '45,000' },
                                                    { name: '베이직 크루넥 티', price: '19,000' },
                                                    { name: '캔버스 토트백', price: '29,000' },
                                                ].map((item, i) => (
                                                    <div key={i} className="rounded-lg border border-gray-200 overflow-hidden">
                                                        <div className="h-10 bg-gradient-to-br from-gray-100 to-gray-200"></div>
                                                        <div className="px-1 py-0.5">
                                                            <div className="text-[6px] text-gray-600 truncate">{item.name}</div>
                                                            <div className="text-[7px] font-bold text-gray-900">{item.price}원</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* 방문자 선택 */}
                                    <div className="px-5 pt-4 pb-5">
                                        <div className="text-[10px] text-gray-500 mb-2">방문자를 바꿔 보세요</div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {HERO_SEGMENTS.map((s, i) => (
                                                <button
                                                    key={s.key}
                                                    onClick={() => setSegIdx(i)}
                                                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold transition-colors ${i === segIdx ? 'bg-white text-gray-900' : 'bg-gray-800 text-gray-400 hover:text-gray-200'}`}
                                                >
                                                    <span className={`w-1.5 h-1.5 rounded-full ${s.classes.dot}`}></span>
                                                    {s.short}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Floating Tooltip */}
                                    <div key={`tip-${seg.key}`} className="absolute top-[42%] -right-2 bg-gray-900/90 backdrop-blur border border-pink-500/30 text-white px-4 py-2 rounded-lg shadow-xl z-20 seg-in">
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="w-2 h-2 bg-pink-400 rounded-full animate-ping"></div>
                                            <span className="text-xs font-bold text-pink-400">이 고객에게만</span>
                                        </div>
                                        <div className="text-sm font-bold">{seg.tipLabel}</div>
                                        <div className="text-xs text-gray-400 mt-0.5">{seg.tipSub}</div>
                                    </div>
                                </div>

                                <div className="absolute -bottom-10 left-10 right-10 h-20 bg-pink-500/20 blur-[60px] rounded-full transform scale-y-50"></div>
                            </div>
                        </div>

                    </div>
                </div>

                <style>{`
          .perspective-1000 { perspective: 1000px; }
          .rotate-y-\\[-12deg\\] { transform: rotateY(-12deg) rotateX(5deg); }
          @keyframes seg-in {
            0%   { opacity: 0; transform: translateY(6px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .seg-in { animation: seg-in 0.45s ease-out both; }
          @media (prefers-reduced-motion: reduce) {
            .seg-in { animation: none; }
          }
        `}</style>
            </section>

            {/* ==========================================
          섹션 2: Why Personalization? — 같은 화면 vs 맞춤 화면
      ========================================== */}
            <section className="py-24 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-block px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                            Why Personalization?
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                            같은 내용이 <br className="sm:hidden" />
                            <span className="text-pink-600">모두에게 맞을 수는 없습니다</span>
                        </h2>
                        <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
                            처음 온 고객, 망설이는 고객, 이미 단골인 고객. 상황이 다른 사람들에게
                            같은 말을 하면 누구에게도 와닿지 않습니다.
                        </p>
                    </div>

                    <div className="relative max-w-5xl mx-auto">
                        <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white rounded-full items-center justify-center shadow-lg z-10 font-black text-gray-300 italic text-xl border-4 border-gray-50">
                            VS
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 md:gap-0">

                            {/* 모두에게 같은 화면 */}
                            <div className="bg-white p-8 md:p-12 rounded-2xl md:rounded-r-none md:rounded-l-2xl border border-gray-200 shadow-sm relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-gray-100 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>
                                <div className="relative z-10">
                                    <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-6 text-gray-400">
                                        <Users className="w-8 h-8" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-500 mb-2">모두에게 같은 화면</h3>
                                    <p className="text-gray-400 text-sm mb-8 font-medium">One Page for Everyone</p>

                                    <ul className="space-y-6">
                                        {[
                                            { text: '처음 온 고객에게 "재구매 감사 쿠폰"', sub: '받을 수 없는 혜택을 보고 그냥 나감' },
                                            { text: '10번 산 단골에게 "첫 구매 할인" 배너', sub: '단골에게는 아무 의미 없는 메시지' },
                                            { text: '원피스 광고를 눌렀는데 메인 화면 도착', sub: '찾던 상품을 다시 검색해야 함' },
                                        ].map((item, idx) => (
                                            <li key={idx} className="flex items-start gap-4 opacity-70 group-hover:opacity-100 transition-opacity">
                                                <div className="mt-1 min-w-[24px]">
                                                    <HelpCircle className="w-6 h-6 text-gray-300" />
                                                </div>
                                                <div>
                                                    <div className="text-gray-600 font-medium text-lg">{item.text}</div>
                                                    <div className="text-gray-400 text-xs mt-0.5">{item.sub}</div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            {/* 고객별 맞춤 화면 */}
                            <div className="bg-[#1A1F2E] p-8 md:p-12 rounded-2xl md:rounded-l-none md:rounded-r-2xl border border-gray-700 shadow-2xl relative overflow-hidden transform md:scale-105 md:-my-4 z-0 ring-1 ring-pink-500/20">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-pink-500/20 to-rose-500/20 rounded-full blur-3xl opacity-60 -mr-20 -mt-20 animate-pulse"></div>
                                <div className="relative z-10">
                                    <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl flex items-center justify-center mb-6 text-white shadow-lg shadow-pink-500/30">
                                        <Sparkles className="w-8 h-8" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-2">고객별 맞춤 화면</h3>
                                    <p className="text-pink-400 text-sm mb-8 font-bold tracking-wide">Right Message, Right Customer</p>

                                    <ul className="space-y-6">
                                        {[
                                            { text: '비회원에게는 가입 혜택부터', sub: '가입해야 할 이유를 바로 보여줌' },
                                            { text: '단골에게는 신상품 우선 공개', sub: '할인 대신 특별함으로' },
                                            { text: '광고 고객에게는 광고 속 그 상품', sub: '클릭한 기대가 그대로 이어짐' },
                                        ].map((item, idx) => (
                                            <li key={idx} className="flex items-start gap-4">
                                                <div className="mt-1 min-w-[24px]">
                                                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                                                        <Check className="w-4 h-4 text-green-400 stroke-[3]" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-white font-bold text-lg">{item.text}</div>
                                                    <div className="text-gray-400 text-xs mt-0.5 font-medium">{item.sub}</div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className="mt-10 pt-8 border-t border-gray-700">
                                        <div className="flex items-center gap-2 text-pink-400 font-bold">
                                            <span>같은 방문, 다른 결과</span>
                                            <ArrowRight className="w-5 h-5" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 3: 고객 유형별 활용 사례
      ========================================== */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-block px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                            Who & What
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                            이런 고객에게, <br className="sm:hidden" />
                            <span className="text-pink-600">이런 화면을</span>
                        </h2>
                        <p className="text-lg sm:text-xl text-gray-600">
                            쇼핑몰에서 가장 자주 만나는 고객별로, <br className="sm:hidden" />바로 써먹을 수 있는 개인화 예시입니다
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {SEGMENT_CASES.map((c, i) => (
                            <div key={i} className={`group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-2xl transition-all duration-300 flex flex-col ${c.classes.hover}`}>
                                {/* 미니 배너 미리보기 */}
                                <div className="h-32 bg-gray-50 rounded-xl mb-6 border border-gray-100 p-3 flex flex-col justify-center gap-2">
                                    <div className={`relative rounded-lg px-3 py-2.5 overflow-hidden shadow-sm group-hover:-translate-y-0.5 transition-transform ${c.classes.banner}`}>
                                        <div className="absolute -right-4 -top-4 w-12 h-12 rounded-full bg-white/10"></div>
                                        <div className={`relative text-[8px] font-bold tracking-widest ${c.classes.eyebrow}`}>{c.eyebrow}</div>
                                        <div className={`relative text-xs font-bold leading-snug mt-0.5 ${c.classes.text}`}>{c.preview}</div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-1.5">
                                        {[0, 1, 2].map(n => (
                                            <div key={n} className="h-6 rounded bg-white border border-gray-200"></div>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 mb-3">
                                    <div className={`p-2 rounded-lg flex-shrink-0 ${c.classes.iconBox}`}>
                                        {c.icon}
                                    </div>
                                    <h3 className="text-base font-bold text-gray-900 leading-snug">{c.who}</h3>
                                </div>
                                <p className="text-gray-600 text-sm mb-4 leading-relaxed flex-1">{c.desc}</p>
                                <ul className="space-y-1.5">
                                    {c.shows.map((s, idx) => (
                                        <li key={idx} className="flex items-center text-xs text-gray-500 font-medium">
                                            <CheckCircle2 className={`w-3 h-3 mr-2 ${c.classes.check}`} /> {s}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 max-w-3xl mx-auto flex items-center gap-4 p-4 sm:p-5 bg-pink-50 border border-pink-100 rounded-2xl">
                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-pink-600 shadow-sm flex-shrink-0">
                            <Headphones className="w-5 h-5" />
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                            회원 여부 · 장바구니 · 구매 이력 · 등급 같은 쇼핑 행동 조건은 쇼핑몰마다 구조가 다릅니다.
                            <strong className="text-gray-900"> 전담 매니저가 사이트에 맞게 직접 세팅</strong>해 드립니다.
                        </p>
                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 4: 작동 방식 — 누구에게, 무엇을, 얼마나
      ========================================== */}
            <section className="py-24 bg-gray-50 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row items-center gap-16">

                        {/* Left: 설명 */}
                        <div className="flex-1 max-w-xl">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-semibold mb-6">
                                <Target className="w-4 h-4" />
                                <span>How it Works</span>
                            </div>

                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                누구에게, 무엇을.<br />
                                <span className="text-pink-600">두 가지만 정하세요</span>
                            </h2>

                            <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                                보여줄 고객을 고르고, 그 고객이 볼 화면을 에디터에서 바꾸면 끝입니다.
                                효과 측정을 위한 비교 그룹까지 한 화면에서 설정합니다.
                            </p>

                            <div className="space-y-6 mb-8">
                                {[
                                    { num: '01', title: '누구에게 — 보여줄 고객 정하기', desc: '비회원, 장바구니 이탈, VIP처럼 원하는 고객을 고릅니다. 조건 여러 개를 함께 걸 수도 있습니다.' },
                                    { num: '02', title: '무엇을 — 보여줄 화면 만들기', desc: '비주얼 에디터에서 배너 · 문구 · 버튼 · 이미지를 클릭으로 바꿉니다. 코딩은 필요 없습니다.' },
                                    { num: '03', title: '얼마나 — 비교 그룹 정하기', desc: '방문자 일부에게는 기존 화면을 그대로 보여주고, 개인화의 실제 효과를 비교합니다.' },
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className="mt-1 w-10 h-10 bg-white border border-pink-100 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-black text-pink-600 shadow-sm">
                                            {item.num}
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                                            <p className="text-sm text-gray-600">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="p-4 bg-white rounded-xl border border-pink-100 shadow-sm">
                                <h4 className="font-semibold text-gray-900 mb-3 text-sm flex items-center gap-2">
                                    <SlidersHorizontal className="w-4 h-4 text-pink-500" />
                                    이런 조건도 함께 조합할 수 있어요
                                </h4>
                                <div className="flex flex-wrap gap-1.5">
                                    {EXTRA_CONDITIONS.map((c) => (
                                        <span key={c} className="px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-xs font-medium text-gray-600">{c}</span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Right: 설정 화면 목업 */}
                        <div className="flex-1 w-full relative">
                            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden relative">
                                <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex justify-between items-center">
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">캠페인 설정</span>
                                    <div className="flex items-center gap-1 text-[10px] font-bold">
                                        <span className="px-2 py-0.5 rounded-full bg-pink-500 text-white">대상</span>
                                        <ChevronRight className="w-3 h-3 text-gray-600" />
                                        <span className="px-2 py-0.5 rounded-full bg-gray-800 text-gray-400">화면</span>
                                        <ChevronRight className="w-3 h-3 text-gray-600" />
                                        <span className="px-2 py-0.5 rounded-full bg-gray-800 text-gray-400">비교 그룹</span>
                                    </div>
                                </div>

                                <div className="p-6 space-y-5">
                                    {/* 캠페인 이름 */}
                                    <div>
                                        <label className="text-xs text-gray-500 font-medium block mb-1.5">캠페인 이름</label>
                                        <div className="h-10 bg-gray-50 border border-gray-200 rounded-lg px-3 flex items-center text-sm text-gray-700 font-medium">
                                            장바구니 이탈 고객 할인 배너
                                        </div>
                                    </div>

                                    {/* 보여줄 고객 */}
                                    <div>
                                        <label className="text-xs text-gray-500 font-medium block mb-1.5">보여줄 고객 <span className="text-gray-400">(모두 충족)</span></label>
                                        <div className="space-y-2">
                                            {[
                                                { type: '쇼핑 행동', cond: '장바구니에 상품이 있음', managed: true },
                                                { type: '쇼핑 행동', cond: '구매 이력 없음', managed: true },
                                                { type: '디바이스', cond: '모바일', managed: false },
                                            ].map((row, i) => (
                                                <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg border border-gray-200 bg-gray-50/60 text-xs">
                                                    <span className="px-2 py-0.5 rounded font-bold bg-pink-100 text-pink-700 flex-shrink-0">{row.type}</span>
                                                    <span className="text-gray-700 font-medium truncate">{row.cond}</span>
                                                    {row.managed && (
                                                        <span className="ml-auto flex items-center gap-1 text-[10px] text-gray-400 flex-shrink-0">
                                                            <Headphones className="w-3 h-3" /> 매니저 세팅
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* 보여줄 화면 */}
                                    <div className="p-3 rounded-lg border border-gray-200 bg-white flex items-center gap-3">
                                        <div className="w-16 h-10 rounded bg-gradient-to-r from-orange-500 to-amber-500 flex-shrink-0 flex items-center px-1.5">
                                            <span className="text-[6px] font-bold text-white leading-tight">담아두신 상품, 오늘까지 10% 할인</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[10px] text-gray-500 font-medium">보여줄 화면</div>
                                            <div className="text-sm font-bold text-gray-900 truncate">장바구니 할인 배너</div>
                                        </div>
                                        <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 text-[10px] font-bold text-gray-600 flex-shrink-0">
                                            <Paintbrush className="w-3 h-3" /> 에디터로 수정
                                        </span>
                                    </div>

                                    {/* 비교 그룹 */}
                                    <div className="p-3 rounded-lg border border-gray-200 bg-white">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="text-[10px] text-gray-500 font-medium">방문자 나누기</div>
                                            <div className="text-[10px] text-gray-400">효과 비교용</div>
                                        </div>
                                        <div className="flex h-3 rounded-full overflow-hidden">
                                            <div className="w-[90%] bg-gradient-to-r from-pink-500 to-rose-500"></div>
                                            <div className="w-[10%] bg-gray-300"></div>
                                        </div>
                                        <div className="mt-2 flex justify-between text-xs">
                                            <span className="font-bold text-pink-700">개인화 화면 90%</span>
                                            <span className="font-medium text-gray-500">기존 화면 10%</span>
                                        </div>
                                    </div>

                                    <button className="w-full py-3 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-xl text-sm font-bold mt-1 flex items-center justify-center gap-2">
                                        저장하고 시작하기
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-pink-100 rounded-full blur-3xl -z-10"></div>
                            <div className="absolute -top-6 -left-6 w-32 h-32 bg-rose-100 rounded-full blur-3xl -z-10"></div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 5: 효과 측정 — 비교 그룹
      ========================================== */}
            <section className="py-24 bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row-reverse items-center gap-16">

                        {/* Right: 설명 (반전) */}
                        <div className="flex-1 max-w-xl">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-rose-100 text-rose-700 rounded-full text-sm font-semibold mb-6">
                                <BarChart3 className="w-4 h-4" />
                                <span>Measured Impact</span>
                            </div>

                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                보여주고 끝나지 않습니다<br />
                                <span className="text-rose-600">효과를 숫자로</span> 확인하세요
                            </h2>

                            <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                                개인화를 켜도 방문자 일부에게는 기존 화면을 그대로 보여줍니다.
                                같은 조건의 고객끼리 비교하니, 개인화가 정말 효과가 있었는지 감이 아니라 숫자로 판단할 수 있습니다.
                            </p>

                            <div className="space-y-6">
                                {[
                                    { title: '비교 그룹 비율 직접 설정', desc: '예를 들어 90%에게는 개인화 화면, 10%에게는 기존 화면. 비율은 언제든 바꿀 수 있습니다.', icon: <SlidersHorizontal className="w-5 h-5 text-rose-600" /> },
                                    { title: '같은 고객끼리 비교', desc: '개인화 대상에 해당하는 고객 안에서만 나눠 비교하므로, 다른 고객이 섞여 결과가 왜곡되지 않습니다.', icon: <Filter className="w-5 h-5 text-rose-600" /> },
                                    { title: '효과가 보이면 확대', desc: '효과가 확인되면 비교 그룹을 줄이고, 아니라면 메시지를 바꿔 다시 시도하세요.', icon: <Zap className="w-5 h-5 text-rose-600" /> },
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className="mt-1 w-10 h-10 bg-rose-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                            {item.icon}
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                                            <p className="text-sm text-gray-600">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Left: 결과 리포트 목업 */}
                        <div className="flex-1 w-full relative">
                            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
                                <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex justify-between items-center">
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">결과 리포트</span>
                                    <span className="px-2 py-0.5 rounded bg-gray-800 text-[10px] font-bold text-gray-400">예시 데이터</span>
                                </div>

                                <div className="p-6">
                                    <div className="text-sm font-bold text-gray-900">장바구니 이탈 고객 할인 배너</div>
                                    <div className="text-xs text-gray-400 mt-0.5">대상: 장바구니에 상품 있음 · 구매 이력 없음 · 최근 14일</div>

                                    <div className="mt-6 space-y-5">
                                        <div>
                                            <div className="flex items-end justify-between mb-1.5">
                                                <div>
                                                    <div className="text-sm font-bold text-pink-700">개인화 화면</div>
                                                    <div className="text-[10px] text-gray-400">방문자 90% · 1,824명</div>
                                                </div>
                                                <div className="text-2xl font-black text-gray-900">4.2<span className="text-sm font-bold text-gray-400">%</span></div>
                                            </div>
                                            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                                                <div className="h-full w-[84%] bg-gradient-to-r from-pink-500 to-rose-500 rounded-full"></div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-end justify-between mb-1.5">
                                                <div>
                                                    <div className="text-sm font-bold text-gray-500">기존 화면</div>
                                                    <div className="text-[10px] text-gray-400">방문자 10% · 203명</div>
                                                </div>
                                                <div className="text-2xl font-black text-gray-400">3.1<span className="text-sm font-bold text-gray-300">%</span></div>
                                            </div>
                                            <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                                                <div className="h-full w-[62%] bg-gray-300 rounded-full"></div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-6 p-4 rounded-xl bg-pink-50 border border-pink-100 flex items-center justify-between">
                                        <div>
                                            <div className="text-[10px] text-pink-700 font-bold uppercase tracking-wide">기존 화면 대비 전환율</div>
                                            <div className="text-2xl font-black text-pink-600">+35%</div>
                                        </div>
                                        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-pink-600 shadow-sm">
                                            <BarChart3 className="w-5 h-5" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-rose-100 rounded-full blur-3xl -z-10"></div>
                            <div className="absolute -top-6 -right-6 w-32 h-32 bg-pink-100 rounded-full blur-3xl -z-10"></div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 6: 주요 특징 벤토 그리드
      ========================================== */}
            <section className="py-24 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                            세팅은 가볍게, 운영은 함께
                        </h2>
                        <p className="text-xl text-gray-600">
                            이미 설치된 스크립트 그대로, <br className="sm:hidden" />어려운 조건은 매니저가 맡습니다.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                        {/* 1. 매니저 세팅 (Large) */}
                        <div className="md:col-span-2 bg-white rounded-2xl p-8 border border-gray-200 relative overflow-hidden group hover:border-pink-200 transition-colors">
                            <div className="grid sm:grid-cols-2 gap-8 items-center">
                                <div className="relative z-10">
                                    <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-6 text-pink-600">
                                        <Headphones className="w-6 h-6" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">쇼핑 행동 조건은 매니저가 세팅</h3>
                                    <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                                        회원 여부, 장바구니, 구매 이력, 등급처럼 쇼핑몰마다 구조가 다른 조건은
                                        전담 매니저가 사이트에 맞게 직접 연결합니다. <strong>원하는 고객을 말로 설명만 해주세요.</strong>
                                    </p>
                                </div>
                                <div className="space-y-3">
                                    <div className="flex justify-end">
                                        <div className="max-w-[240px] px-4 py-2.5 rounded-2xl rounded-br-sm bg-gray-100 text-sm text-gray-700">
                                            장바구니 담고 안 산 고객한테만 쿠폰 배너 보여주고 싶어요
                                        </div>
                                    </div>
                                    <div className="flex items-end gap-2">
                                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white flex-shrink-0">
                                            <Headphones className="w-3.5 h-3.5" />
                                        </div>
                                        <div className="max-w-[240px] px-4 py-2.5 rounded-2xl rounded-bl-sm bg-pink-600 text-sm text-white">
                                            세팅 완료했어요. 이제 보여줄 내용만 만들면 바로 시작됩니다 <Check className="inline w-4 h-4 -mt-0.5" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 2. 코딩 없이 수정 (Dark Tall) */}
                        <div className="md:row-span-2 bg-gray-900 text-white rounded-2xl p-8 border border-gray-800 relative overflow-hidden group">
                            <div className="relative z-10 h-full flex flex-col justify-between">
                                <div>
                                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6">
                                        <Paintbrush className="w-6 h-6 text-pink-400" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2">코딩 없이 화면 수정</h3>
                                    <p className="text-gray-400 text-sm leading-relaxed">
                                        배너 · 문구 · 버튼 · 이미지를<br />
                                        비주얼 에디터에서 클릭으로 바꿉니다.<br />
                                        고객별로 다른 화면을<br />
                                        여러 개 만들어 두세요.
                                    </p>
                                </div>
                                <div className="mt-6 pt-6 border-t border-gray-700 space-y-2 text-sm text-gray-400">
                                    {['텍스트 · 이미지 교체', '스타일 변경', '요소 추가 · 삭제 · 이동', 'HTML 직접 편집'].map((t) => (
                                        <div key={t} className="flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-pink-400" /> {t}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* 3. 추가 설치 없음 */}
                        <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-all duration-300 group">
                            <div className="flex justify-between items-start mb-6">
                                <div className="w-12 h-12 bg-pink-50 rounded-xl flex items-center justify-center text-pink-600">
                                    <Zap className="w-6 h-6" />
                                </div>
                                <div className="text-xs font-mono text-gray-400 bg-gray-50 px-2 py-1 rounded">1 script</div>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">추가 설치 없음</h3>
                            <p className="text-gray-600 text-sm">
                                이미 설치된 SWITCHUP 스크립트 하나로 동작합니다.
                                개인화를 위해 코드를 더 심을 필요가 없습니다.
                            </p>
                        </div>

                        {/* 4. 카페24 · 아임웹 */}
                        <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 border border-pink-100">
                            <div className="flex justify-between items-start mb-6">
                                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-pink-600 shadow-sm">
                                    <Plug className="w-6 h-6" />
                                </div>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">카페24 · 아임웹 연동</h3>
                            <p className="text-gray-600 text-sm">
                                카페24 · 아임웹 앱으로 간편하게 설치하고,
                                설치된 쇼핑몰이라면 바로 시작할 수 있습니다.
                            </p>
                        </div>

                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 7: 퍼널 → 히트맵 → 세션 녹화 → A/B → 개인화 시너지
      ========================================== */}
            <section className="py-24 bg-white relative overflow-hidden">
                <div className="absolute top-1/2 left-0 w-96 h-96 bg-pink-100/50 rounded-full blur-[100px] -translate-y-1/2 -translate-x-1/3"></div>
                <div className="absolute top-1/2 right-0 w-96 h-96 bg-rose-100/50 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">

                        {/* Left: 텍스트 */}
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-50 border border-pink-100 text-pink-600 text-sm font-bold mb-6">
                                <Zap className="w-4 h-4 fill-pink-600" />
                                <span>Full Loop</span>
                            </div>

                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                                찾고, 확인하고, 검증한 답을<br />
                                <span className="text-pink-600">고객별로 적용</span>하세요
                            </h2>

                            <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                                개인화는 분석과 테스트로 찾은 답을 실제 매출로 바꾸는 마지막 단계입니다.
                                누구에게 무엇이 필요한지 찾는 것부터 보여주는 것까지 한 플랫폼에서 이어집니다.
                            </p>

                            <div className="space-y-3">
                                {[
                                    { n: 1, title: '병목 발견 (Funnel)', desc: '"장바구니 → 결제에서 58% 이탈" → 위치 파악', box: 'bg-purple-50/50 border-purple-100', num: 'bg-purple-100 text-purple-600' },
                                    { n: 2, title: '경향 파악 (Heatmap)', desc: '"장바구니 쿠폰 영역에 클릭이 몰림" → 의심 지점', box: 'bg-orange-50/50 border-orange-100', num: 'bg-orange-100 text-orange-600' },
                                    { n: 3, title: '장면 확인 (Session Replay)', desc: '"쿠폰을 찾다가 못 찾고 이탈" → 원인 확정', box: 'bg-cyan-50/50 border-cyan-100', num: 'bg-cyan-100 text-cyan-600' },
                                    { n: 4, title: '가설 검증 (A/B Test)', desc: '"장바구니 할인 배너 vs 기존 화면" → 효과 측정', box: 'bg-blue-50/50 border-blue-100', num: 'bg-blue-100 text-blue-600' },
                                    { n: 5, title: '고객별 적용 (Personalization)', desc: '"장바구니 담고 떠난 고객에게만 노출" → 매출로 연결', box: 'bg-pink-50/50 border-pink-100 ring-1 ring-pink-200', num: 'bg-pink-100 text-pink-600' },
                                ].map((step, i, arr) => (
                                    <React.Fragment key={step.n}>
                                        <div className={`flex items-center gap-4 p-4 rounded-xl border ${step.box}`}>
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${step.num}`}>{step.n}</div>
                                            <div>
                                                <div className="font-bold text-gray-900">{step.title}</div>
                                                <div className="text-sm text-gray-500">{step.desc}</div>
                                            </div>
                                        </div>
                                        {i < arr.length - 1 && <div className="pl-7"><ArrowDown className="w-5 h-5 text-gray-300" /></div>}
                                    </React.Fragment>
                                ))}
                            </div>

                            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
                                <Link to="/service/funnel">
                                    <button className="flex items-center gap-2 text-purple-600 font-bold hover:text-purple-700 transition-colors group">
                                        퍼널 분석 보기
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </Link>
                                <Link to="/service/heatmap">
                                    <button className="flex items-center gap-2 text-orange-600 font-bold hover:text-orange-700 transition-colors group">
                                        히트맵 분석 보기
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </Link>
                                <Link to="/service/replay">
                                    <button className="flex items-center gap-2 text-cyan-600 font-bold hover:text-cyan-700 transition-colors group">
                                        세션 녹화 보기
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </Link>
                                <Link to="/service/ab-test">
                                    <button className="flex items-center gap-2 text-blue-600 font-bold hover:text-blue-700 transition-colors group">
                                        A/B 테스트 보기
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </Link>
                            </div>
                        </div>

                        {/* Right: 시각화 다이어그램 */}
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-br from-pink-100 to-rose-100 rounded-3xl transform -rotate-3 scale-105 opacity-60 blur-xl"></div>

                            <div className="relative bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-xl flex flex-col items-center justify-center h-full min-h-[480px]">
                                <div className="grid grid-cols-4 gap-3 sm:gap-4 w-full mb-4">
                                    {[
                                        { label: '병목 발견', sub: '퍼널', icon: <Filter className="w-6 h-6 text-purple-500 fill-purple-500" />, box: 'bg-purple-50 border-purple-200' },
                                        { label: '경향 파악', sub: '히트맵', icon: <Flame className="w-6 h-6 text-orange-500 fill-orange-500" />, box: 'bg-orange-50 border-orange-200' },
                                        { label: '장면 확인', sub: '세션 녹화', icon: <Video className="w-6 h-6 text-cyan-500 fill-cyan-500" />, box: 'bg-cyan-50 border-cyan-200' },
                                        { label: '가설 검증', sub: 'A/B 테스트', icon: <Split className="w-6 h-6 text-blue-500" />, box: 'bg-blue-50 border-blue-200' },
                                    ].map((n) => (
                                        <div key={n.label} className="text-center group">
                                            <div className={`w-14 h-14 border rounded-2xl flex items-center justify-center mb-2 mx-auto shadow-sm group-hover:scale-110 transition-transform ${n.box}`}>
                                                {n.icon}
                                            </div>
                                            <div className="font-bold text-gray-900 text-xs sm:text-sm">{n.label}</div>
                                            <div className="text-[10px] sm:text-xs text-gray-500 mt-0.5 font-medium">{n.sub}</div>
                                        </div>
                                    ))}
                                </div>

                                <ArrowDown className="w-6 h-6 text-gray-300 my-3" />

                                <div className="w-full bg-gradient-to-r from-pink-50 to-rose-50 border border-pink-300 ring-4 ring-pink-100 rounded-2xl p-5 flex items-center gap-4 mb-8">
                                    <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm flex-shrink-0">
                                        <Sparkles className="w-9 h-9 text-pink-500" />
                                    </div>
                                    <div>
                                        <div className="font-bold text-gray-900 text-lg">고객별 적용</div>
                                        <div className="text-sm text-pink-700 font-bold">개인화</div>
                                        <div className="text-xs text-gray-500 mt-1">찾은 답을 필요한 고객에게만</div>
                                    </div>
                                </div>

                                <div className="bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg animate-bounce flex items-center gap-2 z-10">
                                    <span>🚀</span>
                                    <span>추측 없는 전환율 개선</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 하단 연동 카드 */}
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50/30 p-8 rounded-2xl border border-blue-100 hover:shadow-lg transition-all group">
                            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 text-blue-600 shadow-sm group-hover:scale-110 transition-transform">
                                <Split className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">A/B 테스트 + 개인화</h3>
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                                모두에게 이기는 안이 없어도 괜찮습니다. 신규 고객에게 이긴 안은 신규 고객에게,
                                단골에게 이긴 안은 단골에게. 고객별로 따로 테스트하고, 이긴 화면을 그 고객에게만 보여주세요.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-cyan-50 to-sky-50/30 p-8 rounded-2xl border border-cyan-100 hover:shadow-lg transition-all group">
                            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 text-cyan-600 shadow-sm group-hover:scale-110 transition-transform">
                                <Video className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">세션 녹화 + 개인화</h3>
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                                장바구니에서 망설이다 떠난 고객의 세션을 보면, 그 순간 어떤 말이 필요했는지 보입니다.
                                그 메시지를 같은 행동을 한 고객에게만 보여주세요.
                            </p>
                        </div>
                    </div>

                </div>
            </section>

            {/* ==========================================
          섹션 8: FAQ
      ========================================== */}
            <section className="py-24 bg-gray-50">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <div className="inline-block px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                            FAQ
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900">자주 묻는 질문</h2>
                    </div>

                    <div className="space-y-3">
                        {FAQS.map((f, i) => {
                            const open = openFaq === i;
                            return (
                                <div key={i} className={`bg-white rounded-2xl border transition-colors ${open ? 'border-pink-200 shadow-sm' : 'border-gray-200'}`}>
                                    <button
                                        onClick={() => setOpenFaq(open ? -1 : i)}
                                        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                                        aria-expanded={open}
                                    >
                                        <span className="font-bold text-gray-900">{f.q}</span>
                                        <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform ${open ? 'rotate-180 text-pink-600' : 'text-gray-400'}`} />
                                    </button>
                                    {open && (
                                        <div className="px-6 pb-5 -mt-1 text-sm sm:text-base text-gray-600 leading-relaxed">
                                            {f.a}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ==========================================
          섹션 9: CTA Section
      ========================================== */}
            <section className="relative py-32 overflow-hidden bg-slate-900">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-pink-900/40 via-slate-900 to-slate-900"></div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-pink-600/20 rounded-full blur-[120px] pointer-events-none"></div>
                <div className="absolute top-20 left-20 w-1 h-1 bg-white rounded-full animate-pulse"></div>
                <div className="absolute top-40 right-40 w-1.5 h-1.5 bg-pink-400 rounded-full animate-pulse delay-700"></div>

                <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-sm font-medium mb-8">
                        <Sparkles className="w-4 h-4" />
                        <span>Right message, right customer</span>
                    </div>

                    <h2 className="text-3xl md:text-6xl font-bold text-white mb-8 tracking-tight leading-tight">
                        고객마다 다른 내용, <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-400">오늘부터 보여주세요.</span>
                    </h2>

                    <p className="text-lg md:text-xl text-slate-400 mb-12 max-w-2xl mx-auto leading-relaxed">
                        누구에게 무엇을 보여줄지만 정하세요.<br className="hidden md:block" />
                        세팅은 <br className="sm:hidden" />전담 매니저가 함께합니다.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link to="/freestart" className="w-full sm:w-auto">
                            <button className="w-full px-8 py-4 bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-400 hover:to-rose-500 text-white rounded-xl font-bold text-lg shadow-[0_0_40px_-10px_rgba(236,72,153,0.35)] hover:shadow-[0_0_60px_-15px_rgba(236,72,153,0.55)] hover:-translate-y-1 transition-all flex items-center justify-center gap-2 group">
                                무료로 시작하기
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </Link>
                        <Link to="/contact" className="w-full sm:w-auto">
                            <button className="w-full px-8 py-4 bg-transparent border border-slate-700 text-slate-300 rounded-xl font-medium hover:bg-slate-800 hover:text-white transition-all">
                                도입 문의하기
                            </button>
                        </Link>
                    </div>

                    <div className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-slate-500 font-medium">
                        <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-pink-500" /> 30일 무료 체험
                        </div>
                        <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-pink-500" /> 신용카드 불필요
                        </div>
                        <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-pink-500" /> 전담 매니저 세팅 지원
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ServicePersonalization;